/* =====================================================================
 * PluginPage — 플러그인 관리 페이지 (사이드 패널 → 메인 콘텐츠)
 * ===================================================================== */

import React from 'react';
import styled, { css } from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PluginContent } from './PluginModal';

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const glassPanelStyles = css`
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(18,18,18,0.82)' : 'rgba(255,255,255,0.9)'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.58)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 16px 36px rgba(0,0,0,0.28)'
      : '0 14px 32px rgba(0,0,0,0.08)'};
  backdrop-filter: blur(24px) saturate(165%);
  -webkit-backdrop-filter: blur(24px) saturate(165%);
`;

const PageWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 32px 24px 80px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 24px 16px 60px;
  }
`;

const PageCard = styled.main`
  width: 100%;
  max-width: 900px;
  margin: auto 0;
  ${glassPanelStyles};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusLg};
  padding: 36px 36px 32px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 24px 16px 20px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
`;

const PageTitle = styled.h1`
  font-size: 1.125rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const ContentArea = styled.div`
  width: 100%;
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const PluginPage: React.FC = () => {
  const { t } = useTranslation('common');
  const [searchParams] = useSearchParams();
  const initialPluginId = searchParams.get('plugin');

  return (
    <PageWrap>
      <PageCard>
        <PageHeader>
          <PageTitle>{t('nav.plugin')}</PageTitle>
        </PageHeader>
        <ContentArea>
          <PluginContent initialPluginId={initialPluginId} />
        </ContentArea>
      </PageCard>
    </PageWrap>
  );
};
